import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createEventRegistration,
  getEventOptions,
  sendEventRegistrationToWhatsApp,
} from "../services/siteService";

const emptyForm = {
  eventType: "",
  childName: "",
  parentNumber: "",
  childAge: "",
};

const ageOptions = [
  "1 Year",
  "2 Years",
  "3 Years",
  "4 Years",
  "5 Years",
  "6 Years",
];

/* ─── Mobile: tappable radio pill ─── */
function EventPill({ option, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all ${
        selected
          ? "border-orange-400 bg-orange-50"
          : "border-slate-200 bg-slate-50"
      }`}
    >
      <span
        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all ${
          selected ? "border-orange-500 bg-orange-500" : "border-slate-300"
        }`}
      >
        {selected && (
          <span className="h-2 w-2 rounded-full bg-white" />
        )}
      </span>
      <span className="flex-1 text-sm font-medium text-slate-800">
        {option.label}
      </span>
      <span className="rounded-lg bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
        {option.prefix}1…
      </span>
    </button>
  );
}

/* ─── Field wrapper (mobile card row) ─── */
function Field({ label, error, children }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      {children}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}

/* ─── Shared input className helper ─── */
function inputCls(hasError) {
  return `w-full rounded-2xl border px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-400 ${
    hasError ? "border-red-400" : "border-slate-200"
  }`;
}

export default function EventRegistrationPage() {
  const eventOptions = useMemo(() => getEventOptions(), []);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submittedToken, setSubmittedToken] = useState("");
  const [submittedEvent, setSubmittedEvent] = useState("");
  const [whatsAppUrl, setWhatsAppUrl] = useState("");
  const [isWhatsAppBlocked, setIsWhatsAppBlocked] = useState(false);
  const [mobileTab, setMobileTab] = useState("register"); // "register" | "info"

  const selectedEventObj = eventOptions.find((o) => o.value === form.eventType);

  /* ── Handlers (unchanged logic) ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (name === "parentNumber") {
        return { ...prev, [name]: value.replace(/\D/g, "").slice(0, 10) };
      }

      return { ...prev, [name]: value };
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEventSelect = (value) => {
    setForm((prev) => ({ ...prev, eventType: value }));
    if (errors.eventType) setErrors((prev) => ({ ...prev, eventType: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.eventType) next.eventType = "Please select an event.";
    if (!form.childName.trim()) next.childName = "Child name is required.";
    if (!form.parentNumber.trim()) {
      next.parentNumber = "Parent WhatsApp number is required.";
    } else if (!/^\d{10}$/.test(form.parentNumber.replace(/\D/g, ""))) {
      next.parentNumber = "Enter a valid 10-digit number.";
    }
    if (!form.childAge) next.childAge = "Child age is required.";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const result = await createEventRegistration({
        ...form,
        source: "event-registration-page",
      });
      try {
        const whatsappResult = await sendEventRegistrationToWhatsApp({
          ...result,
          parentNumber: form.parentNumber,
          childName: form.childName,
          childAge: form.childAge,
        });
        setWhatsAppUrl(whatsappResult.url);
        setIsWhatsAppBlocked(!whatsappResult.opened && Boolean(whatsappResult.url));
      } catch {
        setWhatsAppUrl("");
        setIsWhatsAppBlocked(false);
      }
      setSubmittedToken(result.token || "");
      setSubmittedEvent(result.eventLabel || selectedEventObj?.label || "");
      setForm(emptyForm);
      setErrors({});
    } catch (err) {
      setSubmitError(err.message || "Unable to submit event registration.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmittedToken("");
    setSubmittedEvent("");
    setSubmitError("");
    setWhatsAppUrl("");
    setIsWhatsAppBlocked(false);
  };

  /* ════════════════════════════════════════
     DESKTOP LAYOUT (md and above) — unchanged
  ════════════════════════════════════════ */
  const renderDesktopLayout = () => (
    <div className="hidden min-h-screen bg-slate-100 md:block">
      {/* Hero */}
      <section
        className="px-6 py-16 text-white"
        style={{
          background:
            "linear-gradient(135deg, #0f172a 0%, #134e4a 40%, #f97316 100%)",
        }}
      >
        <div className="mx-auto max-w-6xl">
          <p className="text-sm uppercase tracking-[0.35em] text-amber-200">
            Events
          </p>
          <h1 className="mt-4 max-w-3xl text-4xl font-black md:text-5xl">
            Register for your preferred event
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-slate-100 md:text-base">
            Choose one event, submit the child details, and the system
            will generate the next token in order.
          </p>
        </div>
      </section>

      {/* Two-col grid */}
      <section className="mx-auto grid max-w-6xl gap-8 px-4 py-12 lg:grid-cols-[0.95fr_1.05fr]">
        {/* Info card */}
        <article className="rounded-[30px] bg-slate-950 p-8 text-white shadow-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-300">
            Token Rules
          </p>
          <h2 className="mt-4 text-3xl font-black">
            Each event has its own running token.
          </h2>
          <div className="mt-8 space-y-4">
            {eventOptions.map((option) => (
              <div
                key={option.value}
                className="rounded-3xl border border-white/10 bg-white/5 p-5"
              >
                <p className="text-lg font-bold text-white">{option.label}</p>
                <p className="mt-1 text-sm text-slate-300">
                  Tokens generate like{" "}
                  <span className="font-semibold text-amber-300">
                    {option.prefix}1, {option.prefix}2, {option.prefix}3
                  </span>
                </p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-slate-300">
            The event page is available only here and in the footer link, so it
            stays separate from the main navigation.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-white transition hover:border-amber-300 hover:text-amber-200"
          >
            Need help? Contact us
          </Link>
        </article>

        {/* Form card */}
        <article className="rounded-[32px] bg-white p-6 shadow-xl ring-1 ring-slate-200 md:p-8">
          <div className="mb-6">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-teal-700">
              New Registration
            </p>
            <h2 className="mt-2 text-3xl font-black text-slate-900">
              Create event entry
            </h2>
          </div>

          {submittedToken ? (
            <div className="rounded-[28px] bg-emerald-50 p-8 text-center ring-1 ring-emerald-100">
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-700">
                Registration Saved
              </p>
              <h3 className="mt-4 text-4xl font-black text-emerald-900">
                {submittedToken}
              </h3>
              <p className="mt-3 text-sm text-emerald-800">
                Token generated for {submittedEvent || "the selected event"}.
              </p>
              {isWhatsAppBlocked && whatsAppUrl ? (
                <a
                  href={whatsAppUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex rounded-full bg-green-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-green-600"
                >
                  Open WhatsApp
                </a>
              ) : null}
              <button
                type="button"
                onClick={resetForm}
                className="mt-6 rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                Add another registration
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {submitError && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {submitError}
                </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Events
                </label>
                <select
                  name="eventType"
                  value={form.eventType}
                  onChange={handleChange}
                  className={inputCls(!!errors.eventType)}
                >
                  <option value="">Select event</option>
                  {eventOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {errors.eventType && (
                  <p className="mt-1 text-xs text-red-500">{errors.eventType}</p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Child Name
                </label>
                <input
                  name="childName"
                  value={form.childName}
                  onChange={handleChange}
                  placeholder="Enter child name"
                  className={inputCls(!!errors.childName)}
                />
                {errors.childName && (
                  <p className="mt-1 text-xs text-red-500">{errors.childName}</p>
                )}
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Parent WhatsApp Number
                  </label>
                  <input
                    name="parentNumber"
                    value={form.parentNumber}
                    onChange={handleChange}
                    placeholder="Enter parent's WhatsApp number"
                    inputMode="numeric"
                    maxLength={10}
                    className={inputCls(!!errors.parentNumber)}
                  />
                  {errors.parentNumber && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.parentNumber}
                    </p>
                  )}
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Child Age
                  </label>
                  <select
                    name="childAge"
                    value={form.childAge}
                    onChange={handleChange}
                    className={inputCls(!!errors.childAge)}
                  >
                    <option value="">Select age</option>
                    {ageOptions.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                  {errors.childAge && (
                    <p className="mt-1 text-xs text-red-500">{errors.childAge}</p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full bg-orange-500 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-orange-600 disabled:opacity-60"
              >
                {isSubmitting ? "Generating token..." : "Submit Registration"}
              </button>
            </form>
          )}
        </article>
      </section>
    </div>
  );

  /* ════════════════════════════════════════
     MOBILE LAYOUT (below md) — app-like UI
  ════════════════════════════════════════ */
  const renderMobileLayout = () => (
    <div className="flex min-h-screen flex-col bg-slate-100 md:hidden">

      {/* App-style sticky header */}
      <div className="sticky top-0 z-10 bg-slate-950">
        {/* Title bar */}
        <div className="flex items-center gap-3 px-4 pb-3 pt-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-500/20">
            <svg className="h-5 w-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-white">Event Registration</p>
            <p className="text-xs text-slate-400">Select · Fill · Get token</p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-2 px-4 pb-3">
          <button
            type="button"
            onClick={() => setMobileTab("register")}
            className={`flex-1 rounded-xl py-2 text-xs font-semibold transition-all ${
              mobileTab === "register"
                ? "bg-orange-500 text-white"
                : "bg-white/10 text-slate-400"
            }`}
          >
            Register
          </button>
          <button
            type="button"
            onClick={() => setMobileTab("info")}
            className={`flex-1 rounded-xl py-2 text-xs font-semibold transition-all ${
              mobileTab === "info"
                ? "bg-orange-500 text-white"
                : "bg-white/10 text-slate-400"
            }`}
          >
            Token rules
          </button>
        </div>
      </div>

      {/* ── Register tab ── */}
      {mobileTab === "register" && (
        <div className="flex flex-col gap-3 px-4 py-4 pb-10">

          {/* Success state */}
          {submittedToken ? (
            <div className="rounded-3xl bg-white px-6 py-10 text-center shadow-sm ring-1 ring-slate-100">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 ring-1 ring-emerald-200">
                <svg className="h-8 w-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600">
                Registration saved
              </p>
              <p className="mt-3 text-5xl font-black text-emerald-900">
                {submittedToken}
              </p>
              <p className="mt-2 text-sm text-slate-500">
                Token generated for {submittedEvent || "the selected event"}.
              </p>
              {isWhatsAppBlocked && whatsAppUrl ? (
                <a
                  href={whatsAppUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex rounded-2xl bg-green-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-green-600"
                >
                  Open WhatsApp
                </a>
              ) : null}
              <button
                type="button"
                onClick={resetForm}
                className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-6 py-3 text-sm font-semibold text-slate-700"
              >
                + Add another registration
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">

              {submitError && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {submitError}
                </div>
              )}

              {/* Event selection */}
              <div>
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Choose event
                </p>
                <div className="rounded-3xl bg-white p-3 shadow-sm ring-1 ring-slate-100">
                  <div className="flex flex-col gap-2">
                    {eventOptions.map((opt) => (
                      <EventPill
                        key={opt.value}
                        option={opt}
                        selected={form.eventType === opt.value}
                        onSelect={handleEventSelect}
                      />
                    ))}
                  </div>
                  {errors.eventType && (
                    <p className="mt-2 px-1 text-xs text-red-500">
                      {errors.eventType}
                    </p>
                  )}
                </div>
              </div>

              {/* Child details card */}
              <div>
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Child details
                </p>
                <div className="flex flex-col gap-4 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
                  <Field label="Child name" error={errors.childName}>
                    <input
                      name="childName"
                      value={form.childName}
                      onChange={handleChange}
                      placeholder="Enter child's full name"
                      className={inputCls(!!errors.childName)}
                    />
                  </Field>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Age" error={errors.childAge}>
                      <select
                        name="childAge"
                        value={form.childAge}
                        onChange={handleChange}
                        className={inputCls(!!errors.childAge)}
                      >
                        <option value="">Age</option>
                        {ageOptions.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Parent WhatsApp number" error={errors.parentNumber}>
                      <input
                        name="parentNumber"
                        value={form.parentNumber}
                        onChange={handleChange}
                        placeholder="10-digit WhatsApp no."
                        inputMode="numeric"
                        maxLength={10}
                        className={inputCls(!!errors.parentNumber)}
                      />
                    </Field>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-1 w-full rounded-2xl bg-orange-500 py-4 text-sm font-bold text-white transition hover:bg-orange-600 disabled:opacity-60"
              >
                {isSubmitting ? "Generating token…" : "Generate token"}
              </button>
            </form>
          )}
        </div>
      )}

      {/* ── Info tab ── */}
      {mobileTab === "info" && (
        <div className="flex flex-col gap-3 px-4 py-4 pb-10">
          <p className="px-1 text-xs font-semibold uppercase tracking-widest text-slate-400">
            How tokens work
          </p>
          <div className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
            <p className="text-sm leading-relaxed text-slate-600">
              Each event has its own running token counter. Submit a
              registration and you'll receive the next token in sequence
              automatically.
            </p>
          </div>

          <p className="px-1 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Events
          </p>
          <div className="flex flex-col gap-2">
            {eventOptions.map((opt) => (
              <div
                key={opt.value}
                className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-100"
              >
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-orange-50">
                  <svg className="h-4 w-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    {opt.label}
                  </p>
                  <p className="text-xs text-slate-500">
                    Tokens:{" "}
                    <span className="font-semibold text-orange-600">
                      {opt.prefix}1, {opt.prefix}2, {opt.prefix}3…
                    </span>
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
            <p className="text-sm leading-relaxed text-slate-600">
              This page is accessible only here and via the footer link — it
              stays separate from the main navigation.
            </p>
            <Link
              to="/contact"
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-teal-600"
            >
              Need help? Contact us →
            </Link>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {renderDesktopLayout()}
      {renderMobileLayout()}
    </>
  );
}
